import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getAdminOrganization, type AdminOrganization } from '../lib/api'

export type TenantDetailContext = {
  org: AdminOrganization
  reload: () => Promise<void>
}

const TABS = [
  { to: 'overview', label: 'Overview' },
  { to: 'users', label: 'Users' },
]

export default function TenantDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  const navigate = useNavigate()
  const [org, setOrg] = useState<AdminOrganization | null>(null)
  const [loading, setLoading] = useState(true)

  async function load() {
    if (!id || !token) return
    setLoading(true)
    try {
      setOrg(await getAdminOrganization(token, id))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id, token])

  if (loading) return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  if (!org) return <div className="p-6"><p className="text-zinc-500 text-sm">Org tidak ditemukan.</p></div>

  return (
    <div>
      <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800">
        <div className="px-6 pt-5 pb-0">
          <div className="flex items-start justify-between mb-4">
            <div>
              <button
                onClick={() => navigate('/sales/tenants')}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-2"
              >
                <ChevronLeft size={14} />
                Tenants
              </button>
              <h1 className="text-xl font-bold text-white">{org.name}</h1>
              <p className="text-zinc-500 text-sm mt-0.5 font-mono">{org.slug}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 mt-1">
              <button
                onClick={() => navigate(`/sales/tenants/${id}/edit`)}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800 border border-zinc-700"
              >
                Edit Org
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {TABS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? 'text-white border-yellow-500'
                      : 'text-zinc-500 border-transparent hover:text-zinc-300'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
      <Outlet context={{ org, reload: load } satisfies TenantDetailContext} />
    </div>
  )
}
